import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { first, intersection, last } from 'lodash-es';
import { TurnToPageWhiteList } from 'src/editor/component/menu-list/const';
import { getMouseBlock } from 'src/editor/utils/block';
import { getSortedRecordIds } from 'src/hooks/collection-view/use-get-sorted-records';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { getMindMapEngine } from 'src/mind-map/utils/mind-engine-utils';
import { isLikeFoldBlockAndInFold } from 'src/redux/managers/ui/use-fold';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import { $appUiStateCache } from 'src/services/app';
import type { DropInfoState } from 'src/services/app/hook/use-drop-info';
import { getDropInfo } from 'src/services/app/hook/use-drop-info';
import { isCollection, isPageLike } from 'src/utils/block-type-utils';
import { filterDescendantId, getRefBlock, hasSyncBlock } from 'src/utils/block-utils';
import { fixCollectionId } from 'src/utils/fix-collection-id';
import { getDynamicPageId } from 'src/utils/getPageId';
import { selectedBlocksToIds } from 'src/utils/select-block-util';
import { TocType } from 'src/views/main/aside/toc/types';
import {
  checkIsSub,
  getCursorArea,
  getPanel,
  ID_ASIDE_PRIVATE,
  ID_DRIVE,
  ID_TOC_CONTAINER,
  isAsideArea,
  isInRightPanel,
  isLeftRight,
  isTocArea,
  TOC_PRIVATE,
  _setDropInfo,
  isPrivateListArea,
  ID_PRIVATE_CONTAINER,
} from './helper';
import { permissionForbidden } from './permission-forbidden';
import type { Position } from './types';
import { Aside, DRIVE, MainPanel, RightPanel } from './types';

const offset = {
  x: 0,
  y: 0,
};

export const onOver = (
  event: MouseEvent | DragEvent,
  offsetX = 0,
  offsetY = 0,
  sourceRelationParentId?: string
) => {
  if (offset.x === event.x + offsetX && offset.y === event.y + offsetX) return;
  offset.x = event.x + offsetX;
  offset.y = event.y + offsetX;

  const isRight = isInRightPanel(offset.x);
  const panelClass = getPanel(isRight);
  const cursorArea = getCursorArea(offset.x, offset.y, isRight);

  const { blocks } = cache;
  const { selectedBlocks } = cache.ui;
  const dropInfo = getDropInfo();
  const dragToc = dropInfo?.tocInfo?.dragToc;
  const dragTocType = dropInfo?.tocInfo?.dragTocType;
  const dragTocDeep = dropInfo?.tocInfo?.dragTocDeep;
  const ids = dragToc ? [dragToc] : selectedBlocksToIds(selectedBlocks);

  let position: Position | undefined;
  let dropId: string | undefined;
  let dropInChild = false;
  let dropTocType: TocType | undefined;
  let dropTocDeep: number | undefined;
  let target: ReturnType<typeof getMouseBlock> | undefined;
  let viewId: string | undefined;
  let groupValue: string | undefined;
  let subGroupValue: string | undefined;
  let syncId: string | undefined;
  let targetRelationParentId: string | undefined;
  let isBoard = false;
  let isCalendar = false;
  let addSubitem = false;
  let calendarDate: number | undefined = undefined;

  // 如果是向日历视图中进行拖拽
  // if (event instanceof MouseEvent) {
  //   const node = document.elementFromPoint(event.clientX, event.clientY);
  //   if (node) {
  //     const container = (node as HTMLElement).closest('.calendar-container') as HTMLElement | null;
  //     if (container) {
  //       const { targetNode } = updateHoverHighlight(event.clientX, event.clientY, container);
  //       if (targetNode) {
  //         _setDropInfo({
  //           cursorArea,
  //           viewId: container.dataset.viewId,
  //           calendarDate: parseInt(targetNode.dataset.date ?? Date.now().toString(), 10),
  //         });
  //       }

  //       return;
  //     }

  //     // 如果是向日历视图中进行拖拽
  //     const currentHoverDates = document.querySelectorAll('.calender-date.drag-hover-highlight');
  //     currentHoverDates.forEach((node) => node.classList.remove('drag-hover-highlight'));
  //   }
  // }
  if (
    // 收藏、分享 的根不做鉴权（notion 空间分区的根页面拖拽时 也会鉴权，先对齐 notion 做了）
    !(dragTocDeep === 0 && (dragTocType === TocType.FAVORITE || dragTocType === TocType.SHARE)) &&
    ids.some((id) => {
      const block = blocks[id];
      if (!block) return true;
      const { role } = getPermissions(id);
      if (block.parentId === block.spaceId) {
        return role === PermissionRole.READER || role === PermissionRole.NONE;
      }

      return (
        // 父级只读不可拖拽
        getPermissions(block.parentId).role === PermissionRole.READER ||
        role === PermissionRole.READER ||
        role === PermissionRole.NONE
      );
    })
  ) {
    _setDropInfo();
    return;
  }

  const checkAllIdsCanConvert = (ids: string[]) => {
    return ids.every((id) => {
      const block = blocks[id];
      return block && TurnToPageWhiteList.includes(block.type);
    });
  };

  if (event.target instanceof HTMLElement) {
    const emptyDataset = (event.target.closest('[data-is-empty]') as HTMLElement | null)?.dataset;
    if (emptyDataset && emptyDataset.isEmpty) {
      const allIdsCanConvert = checkAllIdsCanConvert(ids);
      if (!allIdsCanConvert) {
        _setDropInfo();
        return;
      }

      const {
        subGroupValue,
        groupValue,
        viewId,
        groupProperty: propertyId,
        subgroupProperty: subGroupPropertyId,
      } = emptyDataset;
      const collectionId = fixCollectionId(viewId ?? '');

      _setDropInfo({
        cursorArea,
        tocInfo: {
          dragToc,
          dragTocDeep,
          dragTocType,
        },
        empty: {
          viewId,
          groupValue,
          subGroupValue,
          subGroupPropertyId,
          propertyId,
          collectionId,
        },
      });
      return;
    }

    const galleryContainer = event.target.closest('[data-is-gallery]') as HTMLElement | undefined;
    if (galleryContainer) {
      const allIdsCanConvert = checkAllIdsCanConvert(ids);
      if (!allIdsCanConvert) {
        _setDropInfo();
        return;
      }
    }
    const boardContainer = event.target.closest('[data-is-board]') as HTMLElement | undefined;
    if (boardContainer) {
      const allIdsCanConvert = checkAllIdsCanConvert(ids);
      if (!allIdsCanConvert) {
        _setDropInfo();
        return;
      }

      const nodeList = boardContainer.querySelectorAll('[data-block-id]');
      const lastNode = nodeList[nodeList.length - 1] as HTMLElement | undefined;
      const firstNode = nodeList[0] as HTMLElement | undefined;
      if (lastNode && firstNode) {
        position = 'bottom';
        let node: HTMLElement | undefined = lastNode;
        for (const n of nodeList) {
          const nodeRect = n.getBoundingClientRect();
          if (offset.y > nodeRect.top && offset.y < nodeRect.bottom) {
            node = n as HTMLElement;
            if (offset.y - nodeRect.top > nodeRect.height / 2) {
              position = 'bottom';
            } else {
              position = 'top';
            }
            break;
          }
        }
        isBoard = true;
        dropId = node.dataset.blockId;
        viewId = node.dataset.viewId;
        targetRelationParentId = node.dataset.relationParentId;
        groupValue = node.dataset.groupValue;
        subGroupValue = node.dataset.subGroupValue;
      } else {
        viewId = boardContainer.dataset.viewId;
        if (viewId) {
          const { sortedRecordIds } = getSortedRecordIds({
            viewId,
            groupValue,
            pageId: getDynamicPageId(),
          });
          position = 'bottom';
          isBoard = true;
          dropId = sortedRecordIds[sortedRecordIds.length - 1];
          groupValue = boardContainer.dataset.groupValue;
          subGroupValue = boardContainer.dataset.subGroupValue;
          targetRelationParentId = boardContainer.dataset.relationParentId;
        }
      }
    }

    const calendarEl = event.target.closest('[data-is-calendar]');
    isCalendar = true;
    if (calendarEl) {
      const viewEl = calendarEl?.querySelector('[data-view-id]');
      if (viewEl instanceof HTMLElement) {
        viewId = viewEl.dataset.viewId;
      }
      const calendarDateEl = event.target.closest('[data-date]') as HTMLElement | undefined;
      if (calendarDateEl && calendarDateEl?.dataset.date) {
        calendarDate = parseInt(calendarDateEl?.dataset.date, 10);
      }
    }
  }

  // 鼠标在网盘页面时
  if (cursorArea === DRIVE.ALL) {
    // 从目录拖拽到网盘时
    if (dragToc && !isPageLike(blocks[dragToc]?.type)) {
      _setDropInfo({ cursorArea });
      return;
    }

    const drive = document.getElementById(ID_DRIVE);
    const { blockId } = getMouseBlock(event, drive);

    if (blockId) {
      const dropBlock = blocks[blockId];
      const dragHasFile = ids.some((id) =>
        [BlockType.FILE, BlockType.EXTERNAL_FILE].includes(blocks[id]?.type ?? BlockType.FILE)
      );
      const dropParentIsCollection = isCollection(dropBlock?.type);
      if (dropBlock && isPageLike(dropBlock.type) && !(dropParentIsCollection && dragHasFile)) {
        dropId = blockId;
        position = 'middle';
      }
    }
  }

  // 鼠标在文档左右两侧时
  if (isLeftRight(cursorArea)) {
    const pageNode = document.querySelector(`.${panelClass} [data-page-id]`);

    // 二分查找 column 块
    if (pageNode) {
      const { pageId } = (pageNode as HTMLElement).dataset;
      const page = pageId ? blocks[pageId] : null;

      const { length } = pageNode.children;
      let left = 0;
      let right = length - 1;
      while (left <= right) {
        const mid = (right + left) >> 1;
        const midNode = pageNode.children[mid];
        if (!midNode) break;

        const midNodeRect = midNode.getBoundingClientRect();
        if (midNodeRect.bottom >= offset.y && midNodeRect.top <= offset.y) {
          const { blockId } = (midNode as HTMLElement).dataset;
          const block = blockId ? blocks[blockId] : null;
          const isCollectionView =
            isCollection(page?.type || block?.type) && checkAllIdsCanConvert(ids);

          // 多维表表格希望在记录左侧拖拽页面时也能显示上下线
          if (isCollectionView) {
            for (const node of midNode.querySelectorAll('[data-block-id]')) {
              const rect = node.getBoundingClientRect();
              const HALF_HEIGHT = rect.height >> 1;
              dropId = (node as HTMLElement).dataset.blockId;
              viewId = (node as HTMLElement).dataset.viewId;
              groupValue = (node as HTMLElement).dataset.groupValue;
              subGroupValue = (node as HTMLElement).dataset.subGroupValue;
              syncId = (node as HTMLElement).dataset.syncId;
              targetRelationParentId = (node as HTMLElement).dataset.relationParentId;
              if (offset.y >= rect.top && offset.y < rect.top + HALF_HEIGHT) {
                position = 'top';
                break;
              }
              if (offset.y >= rect.top + HALF_HEIGHT && offset.y < rect.bottom) {
                position = 'bottom';
                break;
              }
            }
          } else {
            const OFFSET = 120;
            let _node = midNode as HTMLElement | null;

            if (_node && !_node.dataset.blockId) {
              _node = _node.querySelector('[data-block-id]') as HTMLElement;
            }

            if (
              !_node ||
              !_node.dataset.horizontal ||
              offset.x < midNodeRect.left - OFFSET ||
              offset.x > midNodeRect.right + OFFSET
            ) {
              _setDropInfo({ cursorArea });
              return;
            }

            const cursorInLeft = cursorArea === MainPanel.LEFT || cursorArea === RightPanel.LEFT;
            position = cursorInLeft ? 'left' : 'right';
            dropId = _node.dataset.blockId;
            viewId = _node.dataset.viewId;
            groupValue = _node.dataset.groupValue;
            subGroupValue = _node.dataset.subGroupValue;
            syncId = _node.dataset.syncId;
            targetRelationParentId = _node.dataset.relationParentId;
            if (/column-list/.test(_node.className)) {
              const columnIds = Array.from(_node.childNodes)
                .map((n) => (n as HTMLElement | null)?.dataset.columnId)
                .filter(Boolean);
              if (cursorInLeft) {
                dropId = first(columnIds);
              } else {
                dropId = last(columnIds);
              }
            }
          }

          break;
        }
        if (midNodeRect.bottom < offset.y) {
          left = mid + 1;
        }
        if (midNodeRect.top > offset.y) {
          right = mid - 1;
        }
      }
    }
  }

  // 鼠标在目录最下面时
  if (cursorArea === Aside.BOTTOM) {
    const privateTocs = document.getElementById(ID_ASIDE_PRIVATE) as HTMLElement | null;
    const privateBlocks = privateTocs?.querySelectorAll('[data-block-id]') ?? [];

    const canDropInDeepZero = ids.every((id) => {
      const type = blocks[id]?.type;
      return (
        type === BlockType.FOLDER ||
        type === BlockType.PAGE ||
        type === BlockType.COLLECTION_VIEW_PAGE
      );
    });

    if (privateBlocks.length) {
      const { blockId, deep } = (last(privateBlocks) as HTMLElement).dataset;

      let canDrop = deep === '0' ? canDropInDeepZero : false;

      if (blockId) {
        const dropBlock = getRefBlock(blockId) || blocks[blockId];
        if (dropBlock) {
          const parentBlock = blocks[dropBlock.parentId];
          if (parentBlock?.type === BlockType.FOLDER) {
            canDrop = ids.every((id) => {
              const _block = blocks[id];
              const type = _block?.type;
              return type === BlockType.FOLDER || (type === BlockType.FILE && _block?.data.ossName);
            });
          }
          if (parentBlock?.type === BlockType.PAGE) {
            canDrop = true;
          }
        }
      }

      if (canDrop) {
        dropId = blockId;
        dropTocType = TocType.PRIVATE;
        position = 'bottom';
      }
    } else if (canDropInDeepZero) {
      dropId = TOC_PRIVATE;
      dropTocType = TocType.PRIVATE;
      dropTocDeep = 0;
      position = 'bottom';
    }
  }

  // 鼠标在文档最下面时
  if (cursorArea === MainPanel.BOTTOM || cursorArea === RightPanel.BOTTOM) {
    const pageId = getDynamicPageId(isRight ? 'right' : 'left');
    dropId = last(blocks[pageId]?.subNodes);
    position = 'bottom';
  }
  console.log('cursorArea', cursorArea);
  if (isTocArea(cursorArea) || isPrivateListArea(cursorArea)) {
    const tocContainer = document.getElementById(
      isTocArea(cursorArea) ? ID_TOC_CONTAINER : ID_PRIVATE_CONTAINER
    ) as HTMLElement | null;
    console.log('tocContainer', tocContainer);
    target = getMouseBlock(event, tocContainer);
    if (target.blockId && target.node) {
      dropId = target.blockId;
      dropTocType = Number(target.node.dataset.tocType);
      dropTocDeep = Number(target.node.dataset.deep);
      // 目录嵌套结构特殊处理
      target.node = target.node.firstChild as HTMLElement;
    }
  }

  if (cursorArea === MainPanel.MIDDLE || cursorArea === RightPanel.MIDDLE) {
    // 分栏中间
    const colNode = (event.target as HTMLElement | null)?.closest('.col-drop');
    if (colNode) {
      const { columnId } = (colNode as HTMLElement).dataset;
      dropId = columnId;
      position = 'right';
    } else if (!isBoard) {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      const eventTarget = event.target.closest('[data-block-id]') as HTMLElement | null;
      const subitemEl = event.target.closest('[data-add-sub-item]');
      if (subitemEl instanceof HTMLElement) {
        dropId = subitemEl.dataset.addSubItem;
        addSubitem = true;
        viewId = subitemEl.dataset.viewId;
      } else {
        dropInChild = !!(
          (event.target as HTMLElement).closest('[data-drop-in-child]') as HTMLElement | null
        )?.dataset.dropInChild;
        if (eventTarget) {
          target = { blockId: eventTarget.dataset.blockId, node: eventTarget };
        } else {
          target = getMouseBlock(event, document.querySelector(`.${panelClass}`));
        }
        if (target.blockId) {
          dropId = target.blockId;
          // 有可能移动到一个空的日期内,也有可能会移到一个页面内
          if (isCalendar) {
            viewId = target.node?.dataset.viewId || viewId;
          } else {
            viewId = target.node?.dataset.viewId;
          }
          groupValue = target.node?.dataset.groupValue;
          subGroupValue = target.node?.dataset.subGroupValue;
          syncId = target.node?.dataset.syncId;
          targetRelationParentId = target.node?.dataset.relationParentId;

          if (isBoard) {
            position = undefined;
            isBoard = false;
          }
        }
      }
    }
  }

  const _hasSyncBlock = selectedBlocks.some((v) => {
    return hasSyncBlock(v.blockId);
  });
  // 如果是在思维导图的话就先禁掉，后续自己处理
  const mindPage = (event.target as Element | null)?.closest('.mind-page');
  if (mindPage) {
    const { mindPageId } = (mindPage as HTMLDivElement).dataset;
    if (!mindPageId) return;
    const engine = getMindMapEngine(mindPageId);
    if (!engine) return;
    const id = $appUiStateCache.$focusBlockId ?? ids[0];
    if (!id) return;
    let selectBlockIds = ids;
    if (cache.ui.selectedBlocks.length > 0) {
      const filterSelectedBlocks = filterDescendantId(cache.ui.selectedBlocks);
      const focusSelectBlock = cache.ui.selectedBlocks.find(
        (s) => s.blockId === $appUiStateCache.$focusBlockId
      );
      if (!focusSelectBlock) return;
      selectBlockIds = selectedBlocksToIds(filterSelectedBlocks);
      // 如果多个选中块里拖拽了一个子块，那只能focus这个子块，其他块取消选中（参考了飞书）
      if (!selectBlockIds.includes(id)) {
        dispatch(uiActions.updateSelectBlocks([focusSelectBlock]));
      }
    }

    engine.updateDragInfo({
      id,
      ids: selectBlockIds,
      left: event.x + offsetX,
      top: event.y + offsetY,
    });
    const mindMapDropInfo = engine.getState().dropInfo;
    position = undefined;
    dropId = undefined;
    dropId = undefined;
    if (mindMapDropInfo) {
      // 适配现有的拖拽逻辑
      if (mindMapDropInfo.after) {
        position = 'bottom';
        dropId = mindMapDropInfo.after;
      } else if (mindMapDropInfo.before) {
        dropId = mindMapDropInfo.before;
        position = 'top';
      } else if (mindMapDropInfo.last) {
        position = 'middle';
        dropId = mindMapDropInfo.parentId;
      } else if (mindMapDropInfo.first) {
        dropInChild = true;
        position = 'bottom';
        dropId = mindMapDropInfo.parentId;
      }
    }
    // const scroller =(event.target as HTMLElement).closest('next-space-page')
    // if (el) {
    const _dropInfo = {
      viewId,
      groupValue,
      subGroupValue,
      syncId,
      dropId,
      position,
      dropInChild,
      cursorArea,
      mindPageId,
      tocInfo: {
        dragToc,
        dragTocDeep,
        dragTocType,
        dropTocDeep,
        dropTocType,
      },
      addSubitem,
    };
    if (permissionForbidden(_dropInfo, ids)) {
      _setDropInfo();
      return;
    }
    _setDropInfo(_dropInfo);
    return;
  }

  const dropParent = blocks[blocks[dropId ?? '']?.parentId ?? ''];
  const dropParentType = dropParent?.type;
  if (
    dropId &&
    // 1. 不能放置到拖拽中的块上
    (ids.some((id) => checkIsSub(id, dropId)) ||
      // 2. 收藏 根的目录不能 与 其他目录 交互
      (dragTocType === TocType.FAVORITE && dragTocType !== dropTocType && dragTocDeep === 0) ||
      // 3. 分享不可以拖动到其他目录
      (dragTocType === TocType.SHARE && dragTocType !== dropTocType) ||
      (_hasSyncBlock && syncId) ||
      (event instanceof DragEvent && isCollection(dropParentType)))
  ) {
    _setDropInfo({ cursorArea });
    return;
  }

  if (
    cursorArea !== Aside.BOTTOM &&
    (cursorArea === MainPanel.MIDDLE ||
      cursorArea === RightPanel.MIDDLE ||
      isTocArea(cursorArea) ||
      isPrivateListArea(cursorArea)) &&
    dropId &&
    target &&
    !isBoard &&
    // 这里需要判断下是否处于分栏中间
    position !== 'right'
  ) {
    console.log('heihei');
    const rect = target.node?.getBoundingClientRect();
    if (!rect) return;

    const dropBlock = getRefBlock(dropId) || blocks[dropId];
    if (dropBlock) {
      const dropParentIsCollection = isCollection(blocks[dropBlock.parentId]?.type);
      const allIdsCanConvert = checkAllIdsCanConvert(ids);

      let favoriteAndShareCanDrop = true;

      if (dropTocType === TocType.FAVORITE || dropTocType === TocType.SHARE) {
        // 从文档中拽出来的页面块不可以放到 收藏/分享 目录的根
        if (!dragToc) {
          favoriteAndShareCanDrop = dropTocDeep !== 0;
        }

        // 个人、空间分区不能拖动到共享、收藏的根
        if (dragTocType === TocType.PRIVATE || dragTocType === TocType.SPACE) {
          favoriteAndShareCanDrop = dropTocDeep !== 0;
        }

        if (
          (dragTocType === TocType.FAVORITE || dragTocType === TocType.SHARE) &&
          dragTocDeep !== 0
        ) {
          favoriteAndShareCanDrop = dropTocDeep !== 0;
        }
      }

      const canDropInFolder =
        ids.every((id) => isPageLike(blocks[id]?.type)) ||
        ids.every((id) => blocks[id]?.type === BlockType.FILE && blocks[id]?.data.ossName);

      const canDropOnTopBottom = isAsideArea(cursorArea)
        ? // 在目录区域时, pageLike 页 才可以放 普通 根目录
          favoriteAndShareCanDrop && dropBlock.type === BlockType.FOLDER
          ? canDropInFolder
          : ids.every((id) => {
              const _block = blocks[id];
              if (!_block) return false;
              return isPageLike(_block.type);
            })
        : // 文档页里 在内嵌多维表里拖拽 系统文件 | 除了 page 的块 时有上下的限制
          !dropParentIsCollection || allIdsCanConvert;

      // 收藏 / 分享 根页面不能拖入其他页面
      const isRootShareOrFavoritePage =
        dragTocDeep === 0 && (dragTocType === TocType.FAVORITE || dragTocType === TocType.SHARE);

      // 可以直接拖拽到 页面块/文档块/折叠块 上面
      let canDropIn = false;
      if (!isRootShareOrFavoritePage) {
        // 拖拽到文件夹时
        if (dropBlock.type === BlockType.FOLDER && canDropInFolder) {
          canDropIn = true;
        }

        // 拖拽到折叠列表
        if ([BlockType.FOLD_LIST, BlockType.TOGGLE_HEADER].includes(dropBlock.type)) {
          canDropIn = true;
        }

        // 类折叠块
        if (isLikeFoldBlockAndInFold(dropBlock.uuid)) {
          canDropIn = true;
        }

        // 拖拽到多维表页面 目前只支持所有可转换为页面的块！！
        if (isCollection(dropBlock.type) && allIdsCanConvert) {
          canDropIn = true;
        }

        if (dropBlock.type === BlockType.PAGE) {
          if (!dropParentIsCollection) {
            canDropIn = true;
          } else {
            if (intersection(dropParent?.subNodes ?? [], ids).length === 0) {
              canDropIn = true;
            }
          }
        }
      }

      const MULTIPLE = canDropIn ? 4 : 2;
      const diff = rect.height / MULTIPLE;

      if (canDropOnTopBottom && !dropInChild && offset.y < rect.top + diff) {
        position = 'top';
      } else if (
        canDropIn &&
        (!canDropOnTopBottom || (offset.y >= rect.top + diff && offset.y <= rect.bottom - diff))
      ) {
        position = 'middle';
      } else if (canDropOnTopBottom) {
        position = 'bottom';
      }

      if (target.node?.dataset.horizontal) {
        if (offset.x < rect.left) {
          position = 'left';
        }
        if (offset.x > rect.right) {
          position = 'right';
        }

        // 只在画廊中会存在这种 case
        if (target.node.dataset.onlyHorizontal) {
          if (offset.x < rect.left + rect.width / 2) {
            // todo: gallery 视图中，drop 事件时用。over 的时候转为 left 和 right
            position = 'top';
          } else {
            position = 'bottom';
          }
        }
      }
    }
  }

  const _dropInfo: DropInfoState = {
    viewId,
    groupValue,
    subGroupValue,
    syncId,
    dropId,
    position,
    dropInChild,
    cursorArea,
    tocInfo: {
      dragToc,
      dragTocDeep,
      dragTocType,
      dropTocDeep,
      dropTocType,
    },
    targetRelationParentId,
    sourceRelationParentId,
    addSubitem,
    calendarDate,
  };

  if (permissionForbidden(_dropInfo, ids)) {
    _setDropInfo();
    return;
  }

  _setDropInfo(_dropInfo);
};
